import ExcelJS from 'exceljs';
import type { MatrixGroup, MatrixRow } from '../hooks/useTithes';
import { buildMatrix } from '../hooks/useTithes';
import { fetchAllForExport } from '../data/tithesRepository';
import { INSTITUTIONS } from '@/features/institutions/config/institutions';
import { MONTH_NAMES } from '../types';

// ─── Colour palette ────────────────────────────────────────────────────────────
const C = {
  headerBg:     '1A3A5C',
  headerFg:     'FFFFFF',
  nationalBg:   '4A148C',
  districtBg:   '1565C0',
  regionBg:     '01579B',
  institutionBg:'1B5E20',
  rowAlt:       'EEF2F7',
  rowPlain:     'FFFFFF',
  dateBg:       'F5F7FA',
  dateFg:       '607D8B',
  lateBg:       'FFEBEE',
  lateFg:       'C62828',
  oofBg:        'EEEEEE',
  totalBg:      'E3F2FD',
  totalFg:      '0D47A1',
  grandBg:      '1A3A5C',
  grandFg:      'FFFFFF',
  summaryAlt:   'F5F7FA',
};

type Fill = ExcelJS.FillPattern;
function solidFill(hex: string): Fill {
  return { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${hex}` } };
}
function applyHeaderStyle(cell: ExcelJS.Cell, bgHex: string, fgHex = 'FFFFFF') {
  cell.fill = solidFill(bgHex);
  cell.font = { bold: true, color: { argb: `FF${fgHex}` }, size: 10 };
  cell.alignment = { horizontal: 'center', vertical: 'middle' };
  cell.border = { bottom: { style: 'thin', color: { argb: 'FF9E9E9E' } } };
}
function applyGroupStyle(row: ExcelJS.Row, bgHex: string) {
  row.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = solidFill(bgHex);
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    cell.alignment = { vertical: 'middle' };
  });
}
function formatDate(iso: string) {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}
function setCellValue(cell: ExcelJS.Cell, value: string | number | null) {
  cell.value = (value === null || value === '') ? null : value;
}

// ─── Shared: freeze + header row for position-detail sheets ───────────────────
const DETAIL_COLS = 2 + 12 + 1; // cargo + nombre + 12 months + total

function addDetailHeaderRow(ws: ExcelJS.Worksheet, year: number) {
  ws.columns = [
    { width: 26 }, // Cargo
    { width: 26 }, // Nombre
    ...MONTH_NAMES.map(() => ({ width: 11 })),
    { width: 13 }, // TOTAL
  ];
  ws.views = [{ state: 'frozen', ySplit: 1 }];

  const headerRow = ws.addRow([`DIEZMOS ${year}`, '', ...MONTH_NAMES, 'TOTAL Bs.']);
  headerRow.height = 22;
  ws.mergeCells(1, 1, 1, 2);
  headerRow.getCell(1).value = `DIEZMOS ${year}`;
  headerRow.eachCell({ includeEmpty: true }, (cell, col) => {
    applyHeaderStyle(cell, C.headerBg, col >= 3 && col <= 14 ? 'E3F2FD' : C.headerFg);
  });
}

// ─── Tab 1: Full authority detail ─────────────────────────────────────────────
function addDetailSheet(
  wb: ExcelJS.Workbook,
  groups: MatrixGroup[],
  year: number,
) {
  const ws = wb.addWorksheet(`Diezmos ${year}`, {
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
  });

  addDetailHeaderRow(ws, year);

  const GROUP_BG: Record<string, string> = {
    NATIONAL: C.nationalBg,
    DISTRICT: C.districtBg,
  };
  const GROUP_LABEL: Record<string, (g: MatrixGroup) => string> = {
    NATIONAL: () => '▸ DIRECTORIO NACIONAL',
    DISTRICT: (g) => `▸ DISTRITO: ${g.orgCode} — ${g.orgName}`,
  };

  let rowIdx = 0;
  for (const group of groups) {
    const bg    = GROUP_BG[group.orgType] ?? C.districtBg;
    const label = GROUP_LABEL[group.orgType]?.(group) ?? group.orgName;

    const groupRow = ws.addRow([label, '', ...Array(13).fill('')]);
    ws.mergeCells(groupRow.number, 1, groupRow.number, DETAIL_COLS);
    groupRow.height = 18;
    applyGroupStyle(groupRow, bg);
    groupRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };

    for (const row of group.rows) {
      rowIdx++;
      const rowBg = rowIdx % 2 === 0 ? C.rowAlt : C.rowPlain;

      const amounts: (number | string)[] = [];
      const dates: string[] = [];

      for (const cell of row.cells) {
        if (cell.outOfTenure) {
          amounts.push(''); dates.push('');
        } else if (cell.tithe) {
          amounts.push(cell.tithe.amount);
          dates.push(formatDate(cell.tithe.paid_at));
        } else {
          amounts.push(''); dates.push('');
        }
      }

      const total = row.totalPaid > 0 ? row.totalPaid : '';

      const amtRow = ws.addRow([row.roleLabel, row.label, ...amounts, total]);
      amtRow.height = 16;
      amtRow.getCell(1).font = { bold: true, size: 9 };
      amtRow.getCell(1).fill = solidFill(rowBg);
      amtRow.getCell(2).fill = solidFill(rowBg);
      amtRow.getCell(2).font = { size: 9 };

      for (let m = 0; m < 12; m++) {
        const c = amtRow.getCell(3 + m);
        const cellData = row.cells[m];
        if (cellData.outOfTenure) {
          c.fill = solidFill(C.oofBg); c.value = null;
        } else if (cellData.tithe) {
          c.fill = solidFill(cellData.late ? C.lateBg : rowBg);
          c.font = { size: 9, color: { argb: cellData.late ? `FF${C.lateFg}` : 'FF333333' } };
          c.value = cellData.tithe.amount;
          c.numFmt = '#,##0.00';
          c.alignment = { horizontal: 'right' };
        } else {
          c.fill = solidFill(rowBg);
        }
      }

      const totalCell = amtRow.getCell(3 + 12);
      totalCell.fill = solidFill(C.totalBg);
      totalCell.font = { bold: true, color: { argb: `FF${C.totalFg}` }, size: 9 };
      totalCell.alignment = { horizontal: 'right' };
      if (typeof total === 'number') { totalCell.value = total; totalCell.numFmt = '#,##0.00'; }

      const dateRow = ws.addRow(['', 'FECHA DE PAGO', ...dates, '']);
      dateRow.height = 13;
      dateRow.getCell(1).fill = solidFill(C.dateBg);
      dateRow.getCell(2).fill = solidFill(C.dateBg);
      dateRow.getCell(2).font = { italic: true, size: 8, color: { argb: `FF${C.dateFg}` } };
      dateRow.getCell(2).alignment = { horizontal: 'right' };

      for (let m = 0; m < 12; m++) {
        const c = dateRow.getCell(3 + m);
        const cellData = row.cells[m];
        c.fill = solidFill(cellData.outOfTenure ? C.oofBg : C.dateBg);
        c.font = { size: 8, color: { argb: `FF${cellData.late ? C.lateFg : C.dateFg}` } };
        c.alignment = { horizontal: 'center' };
        setCellValue(c, dates[m] || null);
      }
      dateRow.getCell(3 + 12).fill = solidFill(C.dateBg);
      dateRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = { bottom: { style: 'hair', color: { argb: 'FFBDBDBD' } } };
      });
    }
  }

  addGrandTotal(ws, groups, DETAIL_COLS);
}

// ─── Summary sheets (Distritos / Regiones / Instituciones) ────────────────────

interface SummaryRowData {
  cols: string[];            // label columns before months
  months: (number | null)[]; // length 12
  total: number;
}

function addSummarySheet(
  wb: ExcelJS.Workbook,
  sheetName: string,
  headerBg: string,
  labelHeaders: string[],
  rows: SummaryRowData[],
) {
  const labelCount = labelHeaders.length;
  const totalCols  = labelCount + 12 + 1;

  const ws = wb.addWorksheet(sheetName, {
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  ws.columns = [
    ...labelHeaders.map(() => ({ width: 28 })),
    ...MONTH_NAMES.map(() => ({ width: 11 })),
    { width: 13 },
  ];

  const headerRow = ws.addRow([...labelHeaders, ...MONTH_NAMES, 'TOTAL Bs.']);
  headerRow.height = 22;
  headerRow.eachCell({ includeEmpty: true }, (cell, col) => {
    const isMonth = col > labelCount && col <= labelCount + 12;
    applyHeaderStyle(cell, headerBg, isMonth ? 'E3F2FD' : C.headerFg);
  });

  let grandTotal = 0;
  rows.forEach((row, i) => {
    const rowBg = i % 2 === 0 ? C.rowPlain : C.summaryAlt;
    const total = row.total > 0 ? row.total : null;
    const exRow = ws.addRow([...row.cols, ...row.months.map((v) => v ?? null), total]);
    exRow.height = 16;

    // Style label columns
    for (let c = 1; c <= labelCount; c++) {
      exRow.getCell(c).fill = solidFill(rowBg);
      exRow.getCell(c).font = { size: 9, bold: c === 1 };
    }

    // Style month columns
    for (let m = 0; m < 12; m++) {
      const c = exRow.getCell(labelCount + 1 + m);
      c.fill = solidFill(row.months[m] != null ? rowBg : C.oofBg);
      if (row.months[m] != null) {
        c.font = { size: 9 };
        c.numFmt = '#,##0.00';
        c.alignment = { horizontal: 'right' };
      }
    }

    // Style total column
    const tc = exRow.getCell(labelCount + 13);
    tc.fill  = solidFill(C.totalBg);
    tc.font  = { bold: true, color: { argb: `FF${C.totalFg}` }, size: 9 };
    tc.alignment = { horizontal: 'right' };
    if (total != null) { tc.numFmt = '#,##0.00'; }

    exRow.eachCell({ includeEmpty: true }, (cell) => {
      cell.border = { bottom: { style: 'hair', color: { argb: 'FFBDBDBD' } } };
    });

    grandTotal += row.total;
  });

  // Grand total row
  const gt = ws.addRow([
    ...Array(labelCount).fill(labelCount === 1 ? 'TOTAL GENERAL' : 'TOTAL GENERAL').map((v, i) => i === 0 ? v : ''),
    ...Array(12).fill(''),
    grandTotal,
  ]);
  ws.mergeCells(gt.number, 1, gt.number, labelCount);
  gt.height = 20;
  gt.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = solidFill(C.grandBg);
    cell.font = { bold: true, color: { argb: `FF${C.grandFg}` }, size: 10 };
  });
  const gtc = gt.getCell(totalCols);
  gtc.value  = grandTotal;
  gtc.numFmt = '#,##0.00';
  gtc.alignment = { horizontal: 'right' };
}

// ─── Helpers to build SummaryRowData from groups ──────────────────────────────

/** Region / institution / district-org rows: each MatrixRow is already one entity */
function orgSummaryRows(rows: MatrixRow[], labelFn: (r: MatrixRow) => string[]): SummaryRowData[] {
  return rows.map((row) => {
    const months: (number | null)[] = row.cells.map((c) => c.tithe?.amount ?? null);
    const total = months.reduce<number>((s, v) => s + (v ?? 0), 0);
    return { cols: labelFn(row), months, total };
  });
}

// ─── Regions sheet: rows grouped by district with header rows ─────────────────
function addRegionSheet(wb: ExcelJS.Workbook, regionGroups: MatrixGroup[]) {
  const LABEL_COLS = 2; // Código | Región
  const TOTAL_COLS = LABEL_COLS + 12 + 1;

  const ws = wb.addWorksheet('Regiones', {
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  ws.columns = [
    { width: 12 }, // Código
    { width: 28 }, // Región
    ...MONTH_NAMES.map(() => ({ width: 11 })),
    { width: 13 }, // TOTAL
  ];

  // Header row
  const headerRow = ws.addRow(['Código', 'Región', ...MONTH_NAMES, 'TOTAL Bs.']);
  headerRow.height = 22;
  headerRow.eachCell({ includeEmpty: true }, (cell, col) => {
    const isMonth = col > LABEL_COLS && col <= LABEL_COLS + 12;
    applyHeaderStyle(cell, C.regionBg, isMonth ? 'E3F2FD' : C.headerFg);
  });

  let grandTotal = 0;
  let rowIdx     = 0;

  for (const group of regionGroups) {
    // District group header
    const distRow = ws.addRow([`▸ DISTRITO: ${group.orgCode} — ${group.orgName}`, '', ...Array(13).fill('')]);
    ws.mergeCells(distRow.number, 1, distRow.number, TOTAL_COLS);
    distRow.height = 18;
    applyGroupStyle(distRow, C.regionBg);
    distRow.getCell(1).alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };

    for (const row of group.rows) {
      const rowBg = rowIdx % 2 === 0 ? C.rowPlain : C.summaryAlt;
      rowIdx++;

      const months: (number | null)[] = row.cells.map((c) => c.tithe?.amount ?? null);
      const total = months.reduce<number>((s, v) => s + (v ?? 0), 0);
      grandTotal += total;

      const exRow = ws.addRow([row.roleLabel, row.label, ...months.map((v) => v ?? null), total > 0 ? total : null]);
      exRow.height = 16;

      exRow.getCell(1).fill = solidFill(rowBg);
      exRow.getCell(1).font = { bold: true, size: 9 };
      exRow.getCell(2).fill = solidFill(rowBg);
      exRow.getCell(2).font = { size: 9 };

      for (let m = 0; m < 12; m++) {
        const c = exRow.getCell(LABEL_COLS + 1 + m);
        c.fill = solidFill(months[m] != null ? rowBg : C.oofBg);
        if (months[m] != null) { c.font = { size: 9 }; c.numFmt = '#,##0.00'; c.alignment = { horizontal: 'right' }; }
      }

      const tc = exRow.getCell(TOTAL_COLS);
      tc.fill = solidFill(C.totalBg);
      tc.font = { bold: true, color: { argb: `FF${C.totalFg}` }, size: 9 };
      tc.alignment = { horizontal: 'right' };
      if (total > 0) { tc.value = total; tc.numFmt = '#,##0.00'; }

      exRow.eachCell({ includeEmpty: true }, (cell) => {
        cell.border = { bottom: { style: 'hair', color: { argb: 'FFBDBDBD' } } };
      });
    }
  }

  // Grand total
  const gt = ws.addRow(['TOTAL GENERAL', '', ...Array(12).fill(''), grandTotal]);
  ws.mergeCells(gt.number, 1, gt.number, LABEL_COLS);
  gt.height = 20;
  gt.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = solidFill(C.grandBg);
    cell.font = { bold: true, color: { argb: `FF${C.grandFg}` }, size: 10 };
  });
  const gtc = gt.getCell(TOTAL_COLS);
  gtc.value  = grandTotal;
  gtc.numFmt = '#,##0.00';
  gtc.alignment = { horizontal: 'right' };
}

// ─── Grand total footer for detail sheet ──────────────────────────────────────
function addGrandTotal(ws: ExcelJS.Worksheet, groups: MatrixGroup[], totalCols: number) {
  const grandTotal = groups.flatMap((g) => g.rows).reduce((s, r) => s + r.totalPaid, 0);
  const grandRow = ws.addRow(['TOTAL GENERAL', '', ...Array(12).fill(''), grandTotal]);
  ws.mergeCells(grandRow.number, 1, grandRow.number, 2);
  grandRow.height = 20;
  grandRow.eachCell({ includeEmpty: true }, (cell) => {
    cell.fill = solidFill(C.grandBg);
    cell.font = { bold: true, color: { argb: `FF${C.grandFg}` }, size: 10 };
  });
  const gtc = grandRow.getCell(totalCols);
  gtc.value  = grandTotal;
  gtc.numFmt = '#,##0.00';
  gtc.alignment = { horizontal: 'right' };
}

// ─── Main export entry point ──────────────────────────────────────────────────
export async function exportTithesExcel(year: number) {
  const { tithes, positions, districts, regions } = await fetchAllForExport(year);
  const groups = buildMatrix(year, positions as never, districts, regions, INSTITUTIONS, tithes);

  const wb = new ExcelJS.Workbook();
  wb.creator  = 'ADB Admin Portal';
  wb.created  = new Date();

  const nationalGroup    = groups.find((g) => g.orgType === 'NATIONAL');
  const districtGroups   = groups.filter((g) => g.orgType === 'DISTRICT');
  const districtOrgGroup = groups.find((g) => g.orgType === 'DISTRICT_ORG');
  const regionGroups     = groups.filter((g) => g.orgType === 'REGION');
  const institutionGroup = groups.find((g) => g.orgType === 'INSTITUTION');

  // Tab 1 — full authority detail (national + district positions)
  const detailGroups = [
    ...(nationalGroup  ? [nationalGroup]  : []),
    ...districtGroups,
  ];
  if (detailGroups.length > 0) addDetailSheet(wb, detailGroups, year);

  // Tab 2 — district org-level tithes (one row per district)
  if (districtOrgGroup && districtOrgGroup.rows.length > 0) {
    addSummarySheet(
      wb,
      'Distritos',
      C.districtBg,
      ['Código', 'Distrito'],
      orgSummaryRows(districtOrgGroup.rows, (r) => [r.roleLabel, r.label]),
    );
  }

  // Tab 3 — regions grouped by district
  if (regionGroups.length > 0) addRegionSheet(wb, regionGroups);

  // Tab 4 — institutions (one row per institution, flat)
  if (institutionGroup && institutionGroup.rows.length > 0) {
    addSummarySheet(
      wb,
      'Instituciones y Departamentos',
      C.institutionBg,
      ['Institución'],
      orgSummaryRows(institutionGroup.rows, (r) => [r.label]),
    );
  }

  // ── Download ──────────────────────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = `diezmos_${year}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
